import { useState, useEffect } from "react";
import { PlatformLayout } from "../layouts/PlatformLayout";
import { useIdentity } from "../lib/identity";
import {
  getProgramById,
  levelColor,
  type CertificationLevel,
} from "../lib/certifications";
import { fetchUserCertificates, fetchUserBadges, type CertificateRecord, type BadgeRecord } from "../lib/sentinel-api";
import { generateCertificatePDF, generateBadgePNG, buildCertificateData, buildBadgeData } from "../lib/certificate-generator";
import { supabase } from "../lib/supabase";

function LevelPill({ level }: { level: CertificationLevel }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "0.125rem 0.5rem",
        borderRadius: "var(--radius-sm)",
        fontSize: "0.6875rem",
        fontWeight: 700,
        background: `${levelColor(level)}15`,
        color: levelColor(level),
      }}
    >
      {level}
    </span>
  );
}

export default function CertificatesPage() {
  const { identity } = useIdentity();
  const [certificates, setCertificates] = useState<CertificateRecord[]>([]);
  const [badges, setBadges] = useState<BadgeRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!identity.userId) {
      setLoading(false);
      return;
    }
    Promise.all([
      fetchUserCertificates(identity.userId),
      fetchUserBadges(identity.userId),
    ]).then(([certs, bdgs]) => {
      if (certs.length) setCertificates(certs);
      if (bdgs.length) setBadges(bdgs);
      setLoading(false);
    });
  }, [identity.userId]);

  async function handleDownloadCert(cert: CertificateRecord) {
    const program = getProgramById(cert.program_id);
    if (!program || !identity.name) return;

    // Fetch company logo and name if user belongs to an org
    let companyLogoUrl: string | undefined;
    let companyName: string | undefined;
    if (identity.organizationId) {
      const { data: org } = await supabase
        .from("sentinel_organizations")
        .select("name, logo_url")
        .eq("id", identity.organizationId)
        .maybeSingle();
      if (org) {
        companyName = org.name;
        companyLogoUrl = org.logo_url ?? undefined;
      }
    }

    generateCertificatePDF(buildCertificateData(
      identity.name,
      program,
      cert.verification_code,
      cert.issued_at,
      cert.expires_at ?? "",
      companyLogoUrl,
      companyName,
    ));
  }

  function handleDownloadBadge(badge: BadgeRecord) {
    const program = getProgramById(badge.program_id);
    if (!program || !identity.name) return;
    generateBadgePNG(buildBadgeData(
      identity.name,
      program,
      badge.verification_code,
      badge.issued_at,
    ));
  }

  if (loading) {
    return (
      <PlatformLayout>
        <div style={{ textAlign: "center", padding: "var(--space-8)" }}>
          <p className="text-muted">Loading your certificates...</p>
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout>
      <div
        style={{
          background: "linear-gradient(135deg, var(--primary-950), var(--primary-800))",
          borderRadius: "var(--radius-2xl)",
          padding: "var(--space-6) var(--space-5)",
          marginBottom: "var(--space-5)",
          color: "var(--text-on-dark)",
        }}
      >
        <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>Certificates & Badges</h1>
        <p className="lead lead-dark" style={{ fontSize: "1.0625rem", maxWidth: "600px" }}>
          View and download your earned Sentinel certifications and digital badges. Each certificate includes a verification code for employer verification.
        </p>
      </div>

      {/* Certificates grid */}
      <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Certificates</h2>
      {certificates.length === 0 ? (
        <div className="card" style={{ padding: "var(--space-5)", textAlign: "center" }}>
          <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "0.75rem" }}>No certificates earned yet. Complete a certification program to earn your first certificate.</p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "var(--space-3)",
            marginBottom: "var(--space-5)",
          }}
        >
          {certificates.map((cert, idx) => {
            const program = getProgramById(cert.program_id);
            if (!program) return null;
            return (
              <div key={cert.id ?? idx} className="card card-hover" style={{ padding: 0, overflow: "hidden" }}>
                <div
                  style={{
                    padding: "var(--space-4)",
                    background: "linear-gradient(135deg, var(--primary-800), var(--primary-600))",
                    color: "var(--text-on-dark)",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: "2rem", marginBottom: "0.25rem" }}>&#127893;</div>
                  <div style={{ fontSize: "0.6875rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--accent-300)", marginBottom: "0.25rem" }}>
                    Certificate of Completion
                  </div>
                  <div style={{ fontSize: "0.9375rem", fontWeight: 700, marginBottom: "0.25rem" }}>{program.title}</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted-on-dark)" }}>Credential: {cert.verification_code}</div>
                </div>

                <div style={{ padding: "var(--space-3)" }}>
                  <div style={{ display: "flex", gap: "0.375rem", marginBottom: "0.5rem" }}>
                    <LevelPill level={program.level} />
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.75rem" }}>
                    Issued: {cert.issued_at?.split("T")[0] ?? "N/A"}
                    {cert.expires_at ? ` | Expires: ${cert.expires_at.split("T")[0]}` : ""}
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button onClick={() => handleDownloadCert(cert)} className="btn btn-primary" style={{ flex: 1, fontSize: "0.75rem", padding: "0.5rem 0.625rem" }}>
                      Download
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Badges */}
      <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Digital Badges</h2>
      {badges.length === 0 ? (
        <div className="card" style={{ padding: "var(--space-5)", textAlign: "center" }}>
          <p className="text-muted" style={{ fontSize: "0.875rem" }}>No badges earned yet.</p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: "var(--space-3)",
          }}
        >
          {badges.map((badge, idx) => {
            const program = getProgramById(badge.program_id);
            if (!program) return null;
            return (
              <div key={badge.id ?? idx} className="card" style={{ padding: "var(--space-4)", textAlign: "center" }}>
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    margin: "0 auto 0.75rem",
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${levelColor(program.level)}, var(--primary-600))`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.75rem",
                    color: "#fff",
                    boxShadow: "var(--shadow-lg)",
                  }}
                >
                  &#127881;
                </div>
                <div style={{ fontWeight: 700, fontSize: "0.875rem", marginBottom: "0.25rem" }}>{program.industry} {program.level}</div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>Credential: {badge.verification_code}</div>
                <button onClick={() => handleDownloadBadge(badge)} className="btn btn-secondary" style={{ fontSize: "0.75rem", padding: "0.375rem 0.75rem", marginTop: "0.25rem" }}>
                  Download Badge
                </button>
              </div>
            );
          })}
        </div>
      )}
    </PlatformLayout>
  );
}
